import { Overlay } from '@angular/cdk/overlay';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl,
} from '@angular/forms';
import {
    MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
    MatAutocomplete,
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { Subject, debounceTime, filter, map, takeUntil } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ItemDetailsComponent } from 'app/components/item-details/item-details.component';
import { MenuItem } from 'app/models/menu-item';
import { ModalService } from 'app/components/modal/modal.service';
import { PathEnum } from 'app/enums/pathEnum';
import { LanguagesUtils } from 'app/utils/languages.utils';
import { Establishment } from 'app/models/establishment';
import { MenuService } from 'app/modules/menu/menu.service';
import { EstablishmentService } from 'app/modules/establishment/establishment.service';
import { ResponsiveImageService, ImageSize } from 'app/modules/shared/services/responsive-image.service';
import { MenuAnalyticsService } from 'app/services/menu-analytics.service';

@Component({
    selector: 'search',
    templateUrl: './search.component.html',
    encapsulation: ViewEncapsulation.None,
    exportAs: 'fuseSearch',
    animations: fuseAnimations,
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        FormsModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatOptionModule,
        NgTemplateOutlet,
        MatFormFieldModule,
        MatInputModule,
        TranslocoModule
    ],
    providers: [
        {
            provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: () => {
                const overlay = inject(Overlay);
                return () => overlay.scrollStrategies.block();
            },
        },
    ],
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 2;
    @Input() placeholder: string = 'Search...';
    @Output() search: EventEmitter<any> = new EventEmitter<any>();

    opened: boolean = false;
    resultSets: any[];
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _matAutocomplete: MatAutocomplete;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchValue: string = '';

    establishment: Establishment;

    /**
     * Constructor
     */
    constructor(
        private _elementRef: ElementRef,
        private _httpClient: HttpClient,
        private _renderer2: Renderer2,
        private _translocoService: TranslocoService,
        private _modalService: ModalService,
        private _router: Router,
        private _menuService: MenuService,
        private _establishmentService: EstablishmentService,
        private _responsiveImageService: ResponsiveImageService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _menuAnalytics: MenuAnalyticsService
    ) {
        this.establishment = this._menuService.establishment;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'search-appearance-bar': this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened': this.opened,
        };
    }

    /**
     * Show the search icon whenever an establishment is loaded.
     * Parent templates control which search instance is rendered
     * (header for multi-menu, sticky nav for single-menu).
     */
    get isMenuPath(): boolean {
        return !!this.establishment;
    }

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef) {
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if (value) {
            // Give Angular time to complete the change detection cycle
            setTimeout(() => {
                // Focus to the input element without scrolling
                value.nativeElement.focus({ preventScroll: true });

                this.searchValue = value.nativeElement.value;
            });
        }
    }

    /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete) {
        this._matAutocomplete = value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Appearance
        if ('appearance' in changes) {
            // To prevent any issues, close the
            // search after changing the appearance
            this.close();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to establishment updates - establishment is loaded in APP_INITIALIZER
        // but SearchComponent may be created before MenuService resolve runs, so we need
        // to get establishment from EstablishmentService to ensure it's always available
        this._establishmentService.establishment$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((establishment) => {
                this.establishment = establishment;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to the search field value changes
        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounce),
                takeUntil(this._unsubscribeAll),
                map((value) => {
                    // Set the resultSets to null if there is no value or
                    // the length of the value is smaller than the minLength
                    // so the autocomplete panel can be closed
                    if (!value || value.length < this.minLength) {
                        this.resultSets = null;
                    }

                    // Continue
                    return value;
                }),
                // Filter out undefined/null/false statements and also
                // filter out the values that are smaller than minLength
                filter((value) => value && value.length >= this.minLength)
            )
            .subscribe((value) => {
                this._httpClient
                    .post('api/common/search', { query: value })
                    .subscribe((resultSets: any) => {
                        // Store the result sets
                        this.resultSets = resultSets;

                        // Execute the event
                        this.search.next(resultSets);
                    });
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void {
        // Escape
        if (event.code === 'Escape') {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            if (this.appearance === 'bar' && !this._matAutocomplete.isOpen) {
                this.close();
            }
        }
    }

    /**
     * Prevent browser focus-scroll on search buttons inside transformed/sticky containers.
     * The browser's default mousedown focuses the button and scrolls to its DOM position,
     * which is incorrect when the button is inside a height:0 sticky wrapper with transforms.
     */
    onSearchButtonMousedown(event: MouseEvent): void {
        event.preventDefault();
    }

    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void {
        // Return if it's already opened
        if (this.opened) {
            return;
        }

        // Open the search
        this.opened = true;
    }

    /**
     * Close the search
     * * Used in 'bar'
     */
    close(): void {
        // Return if it's already closed
        if (!this.opened) {
            return;
        }

        // Clear the search input
        this.searchControl.setValue('');

        // Close the search
        this.opened = false;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * Open the item details modal, navigating to the correct menu page first if needed
     */
    openItem(item: MenuItem) {
        this.close();

        // Second entry point into a dish detail sheet, distinct from the menu list. The search
        // query itself is never sent: it is free text typed by a human.
        this.trackSearchOpen(item);

        const itemEl = document.querySelector(`[data-item-id="${item.uuid}"]`) as HTMLElement;

        if (itemEl) {
            // Item is on the current page — scroll to it and open modal
            this.scrollToItemAndOpenModal(itemEl, item);
        } else {
            // Item is not on this page — navigate to the correct menu first
            const menuUuid = this.findMenuUuidForItem(item);
            if (menuUuid) {
                const slug = this._router.url.split('/').filter(s => s)[0].split('?')[0];
                this._router.navigate([slug, PathEnum.MENU, menuUuid]).then(() => {
                    this.waitForItemAndScroll(item.uuid, item);
                });
            } else {
                this._modalService.openModal(ItemDetailsComponent, item);
            }
        }
    }

    private scrollToItemAndOpenModal(itemEl: HTMLElement, item: MenuItem): void {
        const scrollContainer = document.querySelector('.phone-screen-size') as HTMLElement;
        if (scrollContainer) {
            // Measure the actual bottom of all sticky/fixed nav elements to get the content offset
            const itemRect = itemEl.getBoundingClientRect();
            const header = document.querySelector('.z-49');
            const stickyNavInner = document.querySelector('.sticky-nav-inner');
            const menuChipsWrapper = document.querySelector('[appDragScroll]')?.parentElement;
            const navBottom = Math.max(
                header?.getBoundingClientRect().bottom || 0,
                stickyNavInner?.getBoundingClientRect().bottom || 0,
                menuChipsWrapper?.getBoundingClientRect().bottom || 0,
            );
            const scrollTop = scrollContainer.scrollTop + (itemRect.top - navBottom);
            scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
        setTimeout(() => {
            this._modalService.openModal(ItemDetailsComponent, item);
        }, 500);
    }

    /**
     * Counts the opening of a dish reached from the search, resolving the menu and the section it
     * belongs to so the fact row carries the section at event time.
     */
    private trackSearchOpen(item: MenuItem): void {
        let menuUuid: string | undefined;
        let categoryUuid: string | undefined;

        for (const menu of this.establishment?.menus || []) {
            for (const category of menu.categories || []) {
                if (category.items?.some(i => i.uuid === item?.uuid)) {
                    menuUuid = menu.uuid;
                    categoryUuid = category.uuid;
                    break;
                }
            }
            if (menuUuid) {
                break;
            }
        }

        this._menuAnalytics.trackItemOpen(item?.uuid, categoryUuid, menuUuid);
    }

    private findMenuUuidForItem(item: MenuItem): string | null {
        if (!this.establishment?.menus) return null;
        for (const menu of this.establishment.menus) {
            for (const category of menu.categories || []) {
                if (category.items?.some(i => i.uuid === item.uuid)) {
                    return menu.uuid;
                }
            }
        }
        return null;
    }

    private waitForItemAndScroll(uuid: string, item: MenuItem, attempts = 0): void {
        const el = document.querySelector(`[data-item-id="${uuid}"]`) as HTMLElement;
        if (el) {
            this.scrollToItemAndOpenModal(el, item);
        } else if (attempts < 10) {
            setTimeout(() => this.waitForItemAndScroll(uuid, item, attempts + 1), 200);
        } else {
            this._modalService.openModal(ItemDetailsComponent, item);
        }
    }

    /**
     * On option selected
     *
     * @param event
     */
    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        event.option.deselect(); // Deselect the option
        this.searchControl.setValue(this.searchValue, { emitEvent: true }); // Keep the input empty
    }

    getResultTitle(result: any): string {
        // Return empty string if result doesn't have values
        if (!result?.values || !Array.isArray(result.values)) {
            return '';
        }
        const lang = this.establishment ? LanguagesUtils.getMenuRemoteLang(this.establishment) : null;
        const title = (lang && result.values.find(v => v.languageRefCode === lang)?.name)
            || result.values[0]?.name
            || '';
        return this.truncateText(title, 40);
    }

    /**
     * Get the result description based on establishment language
     *
     * @param result
     */
    getResultDescription(result: any): string {
        // Return empty string if result doesn't have values
        if (!result?.values || !Array.isArray(result.values)) {
            return '';
        }
        const lang = this.establishment ? LanguagesUtils.getMenuRemoteLang(this.establishment) : null;
        const description = (lang && result.values.find(v => v.languageRefCode === lang)?.description)
            || result.values[0]?.description
            || '';
        return this.truncateText(description, 40);
    }

    /**
     * Truncate text with ellipsis if it exceeds the maximum length
     *
     * @param text The text to truncate
     * @param maxLength Maximum length before truncation
     * @returns Truncated text with ellipsis if needed
     */
    private truncateText(text: string, maxLength: number): string {
        if (!text) return '';
        
        // Remove HTML tags first
        const cleanText = text.replace(/<[^>]*>/g, '');
        
        if (cleanText.length <= maxLength) {
            return cleanText;
        }
        
        // Find the last space before maxLength to avoid cutting words
        const truncated = cleanText.substring(0, maxLength);
        const lastSpace = truncated.lastIndexOf(' ');
        
        if (lastSpace > 0 && lastSpace > maxLength * 0.8) {
            return truncated.substring(0, lastSpace) + '...';
        }
        
        return truncated + '...';
    }



    /**
     * Handle image loading error by hiding the image
     *
     * @param event
     */
    onImageError(event: any): void {
        // Hide the broken image - the template will show the default icon instead
        event.target.style.display = 'none';
    }

    /**
     * Check if image has loaded successfully
     *
     * @param result
     */
    hasValidImage(result: any): boolean {
        return result.image && result.image.trim() !== '';
    }

    /**
     * Gets the URL for a result's image at appropriate size
     * @param result The search result item
     * @returns The URL to the image
     */
    getResultImageUrl(result: any): string {
        return result?.image ? this._responsiveImageService.getImageUrl(result.image, ImageSize.MEDIUM, this.establishment?.planCode) : '';
    }

    /**
     * Gets the srcset for responsive images
     * @param result The search result item
     * @returns The srcset attribute value
     */
    getResultImageSrcSet(result: any): string {
        return result?.image ? `${this._responsiveImageService.getImageUrl(result.image, ImageSize.MEDIUM, this.establishment?.planCode)} 800w` : '';
    }

    /**
     * Gets the fallback image URL for browsers that don't support WebP
     * @param result The search result item
     * @returns The fallback image URL
     */
    getResultFallbackImageUrl(result: any): string {
        return result?.image ? this._responsiveImageService.getFallbackImageUrl(result.image, ImageSize.MEDIUM, this.establishment?.planCode) : '';
    }
}
